import { useState } from 'react';
import BookingNavbar from './components/BookingNavbar';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const bookingSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Book a Pet Care Appointment - Fluffy Pets',
  url: `${SITE_URL}/booking`,
  description:
    'Schedule a grooming, boarding, veterinary or training appointment for your pet at Fluffy Pets San Francisco. Easy online booking.',
  isPartOf: { '@type': 'WebSite', name: 'Fluffy Pets', url: SITE_URL },
  potentialAction: {
    '@type': 'ReserveAction',
    target: `${SITE_URL}/booking`,
    result: { '@type': 'Reservation', name: 'Pet Care Appointment' },
  },
};

// ... existing code ...

export default function BookingPage() {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    petName: '',
    petType: '',
    service: '',
    date: '',
    time: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const form = e.currentTarget;
      const formDataToSend = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d5rlu3s98t1oshnre9s0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as unknown as Record<string, string>).toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ customerName: '', email: '', phone: '', petName: '', petType: '', service: '', date: '', time: '', notes: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Book a Pet Grooming Appointment San Francisco | Fluffy Pets"
        description="Book a pet grooming, boarding, or veterinary appointment online at Fluffy Pets. Easy scheduling, flexible times, and expert pet care in San Francisco. Reserve your slot today!"
        keywords="book pet grooming San Francisco, pet appointment online, dog grooming booking, pet boarding reservation"
        canonical={`${SITE_URL}/booking`}
        schema={bookingSchema}
      />
      <BookingNavbar />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Book an Appointment</h1>
            <p className="text-lg text-gray-600">Schedule a grooming session for your beloved pet</p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <form onSubmit={handleSubmit} data-readdy-form id="pet-booking-form">
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                    <input type="text" id="customerName" name="customerName" value={formData.customerName} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="John Doe" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="john@example.com" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+971 50 123 4567" />
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Pet Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="petName" className="block text-sm font-medium text-gray-700 mb-2">Pet Name *</label>
                    <input type="text" id="petName" name="petName" value={formData.petName} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Fluffy" />
                  </div>
                  <div>
                    <label htmlFor="petType" className="block text-sm font-medium text-gray-700 mb-2">Pet Type *</label>
                    <select id="petType" name="petType" value={formData.petType} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                      <option value="">Select pet type</option>
                      <option value="Dog">Dog</option>
                      <option value="Cat">Cat</option>
                      <option value="Rabbit">Rabbit</option>
                      <option value="Bird">Bird</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Service Details</h2>
                <div className="mb-6">
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Select Service *</label>
                  <select id="service" name="service" value={formData.service} onChange={handleChange} required
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                    <option value="">Choose a service</option>
                    <option value="Basic Grooming">Basic Grooming</option>
                    <option value="Premium Grooming">Premium Grooming</option>
                    <option value="Spa Treatment">Spa Treatment</option>
                    <option value="Nail Trimming">Nail Trimming</option>
                    <option value="Teeth Cleaning">Teeth Cleaning</option>
                    <option value="Full Package">Full Package</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                    <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                    <select id="time" name="time" value={formData.time} onChange={handleChange} required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                      <option value="">Select time</option>
                      <option value="09:00 AM">09:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="01:00 PM">01:00 PM</option>
                      <option value="02:00 PM">02:00 PM</option>
                      <option value="03:00 PM">03:00 PM</option>
                      <option value="04:00 PM">04:00 PM</option>
                      <option value="05:00 PM">05:00 PM</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mb-8">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">Special Requests or Notes</label>
                <textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={4} maxLength={500}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="Any special requirements or information we should know about your pet..."></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 characters</p>
              </div>
              <button type="submit" disabled={isSubmitting}
                className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap">
                {isSubmitting ? 'Submitting...' : 'Book Appointment'}
              </button>
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-center font-medium">
                    Booking submitted successfully! We&apos;ll contact you shortly to confirm your appointment.
                  </p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-center font-medium">
                    Something went wrong. Please try again or contact us directly.
                  </p>
                </div>
              )}
            </form>
          </div>
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>Need help? Contact us at <a href="mailto:rami@fluffypets.co" rel="nofollow" className="text-teal-600 hover:text-teal-700 font-medium">rami@fluffypets.co</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
